import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { authAPI, mfaAPI } from '../utils/supabase'
import { hideInitialLoaderImmediate } from '../utils/initialLoaderManager'

// Steps for the reset password flow
const RESET_STEPS = {
  LOADING: 'loading',
  READY: 'ready',
  UPDATING: 'updating',
  SUCCESS: 'success',
  ERROR: 'error'
}

export default function ResetPasswordForm() {
  const [step, setStep] = useState(RESET_STEPS.LOADING)
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  })
  const [error, setError] = useState(null)

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaCode, setMfaCode] = useState('')

  const navigate = useNavigate()
  const abortControllerRef = useRef(null)
  const hasRecoverySession = useRef(false)
  const redirectTimeoutRef = useRef(null)

  // Hide the initial loader immediately - this is a standalone page
  useEffect(() => {
    hideInitialLoaderImmediate()
  }, [])

  // Cleanup redirect timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [])

  // Listen for PASSWORD_RECOVERY auth event and check for existing session
  useEffect(() => {
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    // Set up auth state change listener
    const { data: { subscription } } = authAPI.onAuthStateChange(async (event, session) => {
      if (signal.aborted) return

      if (event === 'PASSWORD_RECOVERY') {
        // User has valid recovery session
        hasRecoverySession.current = true

        // Check if MFA is enabled
        const { factors } = await mfaAPI.getVerifiedFactors()

        if (signal.aborted) return

        if (factors && factors.length > 0) {
          setMfaRequired(true)
        }
        setStep(RESET_STEPS.READY)
      }
    })

    // Check for existing session immediately - don't wait for event
    // The event may have already fired before this component mounted
    const checkExistingSession = async () => {
      if (signal.aborted) return

      // Check if there's an existing session (user landed here with a valid recovery token)
      const { session } = await authAPI.getSession()

      if (signal.aborted) return

      // If we already caught the PASSWORD_RECOVERY event, no need to proceed
      if (hasRecoverySession.current) return

      if (session) {
        // Session exists - this is from the recovery link
        // The PASSWORD_RECOVERY event may have fired before component mounted
        hasRecoverySession.current = true

        // Check if MFA is enabled and needs verification
        const { factors } = await mfaAPI.getVerifiedFactors()

        if (signal.aborted) return

        if (factors && factors.length > 0) {
          // User has MFA enabled - show MFA field on the same form
          setMfaRequired(true)
        }
        setStep(RESET_STEPS.READY)
      } else {
        // No session - invalid or expired link
        setStep(RESET_STEPS.ERROR)
        setError('This password reset link is invalid or has expired.')
      }
    }

    checkExistingSession()

    return () => {
      subscription.unsubscribe()
      abortControllerRef.current?.abort()
    }
  }, [])

  // Password strength validation (matches ChangePasswordModal)
  const validatePasswordStrength = (password) => {
    const feedback = []
    let score = 0

    if (password.length >= 8) {
      score += 1
    } else {
      feedback.push('At least 8 characters')
    }

    if (/[a-z]/.test(password)) {
      score += 1
    } else {
      feedback.push('One lowercase letter')
    }

    if (/[A-Z]/.test(password)) {
      score += 1
    } else {
      feedback.push('One uppercase letter')
    }

    if (/\d/.test(password)) {
      score += 1
    } else {
      feedback.push('One number')
    }

    if (/[^a-zA-Z0-9]/.test(password)) {
      score += 1
    } else {
      feedback.push('One special character')
    }

    setPasswordStrength({ score, feedback })
  }

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target
    setPasswordForm(prev => ({ ...prev, [name]: value }))

    if (name === 'newPassword') {
      validatePasswordStrength(value)
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }

    // Clear any previous error
    if (error) {
      setError(null)
    }
  }

  const validatePasswordForm = () => {
    const errors = {}

    // Validate MFA code if required
    if (mfaRequired) {
      if (!mfaCode) {
        errors.mfaCode = 'Verification code is required'
      } else if (mfaCode.length !== 6) {
        errors.mfaCode = 'Please enter a 6-digit code'
      }
    }

    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required'
    } else if (passwordStrength.score < 5) {
      errors.newPassword = 'Password does not meet requirements'
    }

    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password'
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()

    if (!validatePasswordForm() || step === RESET_STEPS.UPDATING) {
      return
    }

    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setStep(RESET_STEPS.UPDATING)

    try {
      if (signal.aborted) return

      // If MFA is required, verify the code first to upgrade to AAL2
      if (mfaRequired) {
        const { factors } = await mfaAPI.getVerifiedFactors()
        if (!factors || factors.length === 0) {
          throw new Error('No MFA factors available')
        }

        const factor = factors[0]

        // Create MFA challenge
        const { data: challengeData, error: challengeError } = await mfaAPI.createChallenge(factor.id)

        if (challengeError) throw challengeError

        // Verify challenge to upgrade session to AAL2
        const { error: verifyError } = await mfaAPI.verifyChallenge(
          factor.id,
          challengeData.id,
          mfaCode
        )

        if (verifyError) throw verifyError

        if (signal.aborted) return
      }

      // Now update the password (session is AAL2 if MFA was verified)
      const { error: updateError } = await authAPI.updatePassword(passwordForm.newPassword)

      if (signal.aborted) return

      if (updateError) throw updateError

      // Sign out the recovery session so user can log in fresh with new password
      await authAPI.signOut()

      if (signal.aborted) return

      setStep(RESET_STEPS.SUCCESS)

      // Redirect to login after 3 seconds
      redirectTimeoutRef.current = setTimeout(() => {
        if (!signal.aborted) {
          navigate('/login')
        }
      }, 3000)

    } catch (err) {
      if (!signal.aborted) {
        setStep(RESET_STEPS.READY)
        // Provide user-friendly error messages
        let message = err.message || 'Failed to update password. Please try again.'
        if (message.includes('Invalid TOTP') || message.includes('invalid code')) {
          message = 'Invalid verification code. Please check and try again.'
        }
        setError(message)
      }
    }
  }

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return 'bg-status-danger'
    if (passwordStrength.score <= 3) return 'bg-status-warning'
    if (passwordStrength.score <= 4) return 'bg-status-info'
    return 'bg-status-success'
  }

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 2) return 'Weak'
    if (passwordStrength.score <= 3) return 'Fair'
    if (passwordStrength.score <= 4) return 'Good'
    return 'Strong'
  }

  // Loading state
  if (step === RESET_STEPS.LOADING) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="apple-card animate-slide-in px-5 py-8">
            <div className="text-center space-y-4">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="apple-text-body text-text-muted">Verifying your reset link...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state (invalid/expired link)
  if (step === RESET_STEPS.ERROR) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="apple-card animate-slide-in px-5 py-8">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-status-danger rounded-full flex items-center justify-center mx-auto">
                <ExclamationTriangleIcon className="w-6 h-6 text-white" />
              </div>
              <h1 className="apple-text-title">Reset Link Invalid</h1>
              <p className="apple-text-body text-text-muted">
                {error || 'This password reset link is invalid or has expired.'}
              </p>
              <Link
                to="/forgot-password"
                className="apple-button-primary w-full flex items-center justify-center mt-4"
              >
                Request New Reset Link
              </Link>
            </div>
          </div>
          <div className="text-center mt-6">
            <p className="apple-text-body">
              Remember your password?{' '}
              <Link
                to="/login"
                className="text-accent-foreground hover:text-status-info font-medium transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (step === RESET_STEPS.SUCCESS) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="apple-card animate-slide-in px-5 py-8">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-status-success rounded-full flex items-center justify-center mx-auto">
                <CheckCircleIcon className="w-6 h-6 text-white" />
              </div>
              <h1 className="apple-text-title">Password Updated</h1>
              <p className="apple-text-body text-text-muted">
                Your password has been successfully updated. Redirecting you to the login page...
              </p>
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Ready state - show password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="apple-card animate-slide-in px-5 py-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="apple-text-title">Create New Password</h1>
            <p className="apple-text-body text-text-muted mt-2">
              Enter your new password below.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-accent-danger border border-status-danger rounded-lg p-3 mb-6">
              <p className="text-sm text-status-danger">{error}</p>
            </div>
          )}

          {/* Password Form */}
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            {/* MFA Code - only shown if user has MFA enabled */}
            {mfaRequired && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheckIcon className="h-4 w-4 text-status-info" />
                  <label htmlFor="mfaCode" className="block text-sm font-medium text-label-secondary">
                    Authenticator Code
                  </label>
                </div>
                <input
                  id="mfaCode"
                  name="mfaCode"
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  className={`apple-input text-center font-mono text-lg ${
                    formErrors.mfaCode ? 'apple-input-error' : ''
                  }`}
                  value={mfaCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setMfaCode(value)
                    if (formErrors.mfaCode) {
                      setFormErrors(prev => ({ ...prev, mfaCode: '' }))
                    }
                    if (error) {
                      setError(null)
                    }
                  }}
                  autoComplete="one-time-code"
                  disabled={step === RESET_STEPS.UPDATING}
                />
                {formErrors.mfaCode && (
                  <p className="text-xs text-status-danger mt-1">{formErrors.mfaCode}</p>
                )}
                <p className="text-xs text-text-muted">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
            )}

            {/* New Password */}
            <div className="space-y-2">
              <label htmlFor="newPassword" className="block text-sm font-medium text-label-secondary">
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  className={`apple-input pr-12 ${
                    formErrors.newPassword ? 'apple-input-error' : ''
                  }`}
                  placeholder="Enter new password"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordInputChange}
                  disabled={step === RESET_STEPS.UPDATING}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  disabled={step === RESET_STEPS.UPDATING}
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {passwordForm.newPassword && (
                <div className="mt-2">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="flex-1 bg-surface-secondary rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score <= 2 ? 'text-status-danger' :
                      passwordStrength.score <= 3 ? 'text-status-warning' :
                      passwordStrength.score <= 4 ? 'text-status-info' : 'text-status-success'
                    }`}>
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="text-xs text-text-secondary">
                      Missing: {passwordStrength.feedback.join(', ')}
                    </div>
                  )}
                </div>
              )}

              {formErrors.newPassword && (
                <p className="text-xs text-status-danger mt-1">{formErrors.newPassword}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-label-secondary">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`apple-input pr-12 ${
                    formErrors.confirmPassword ? 'apple-input-error' : ''
                  }`}
                  placeholder="Confirm new password"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordInputChange}
                  disabled={step === RESET_STEPS.UPDATING}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={step === RESET_STEPS.UPDATING}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p className="text-xs text-status-danger mt-1">{formErrors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={step === RESET_STEPS.UPDATING}
              className="apple-button-primary w-full flex items-center justify-center focus-apple disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {step === RESET_STEPS.UPDATING && (
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {step === RESET_STEPS.UPDATING ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        </div>

        {/* Back to Login Link */}
        <div className="text-center mt-6">
          <p className="apple-text-body">
            Remember your password?{' '}
            <Link
              to="/login"
              className="text-accent-foreground hover:text-status-info font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
